<template>
  <div v-if="value" class="give-badge-dialog-overlay" @click.self="handleOverlayClick">
    <div class="give-badge-dialog">
      <!-- Modal Header -->
      <div class="modal-header">
        <div class="header-content">
          <div class="featured-icon">
            <img :src="require('@components/assets/icons/badge.svg')" alt="badge icon" />
          </div>
        </div>
        <button class="close-button" @click="handleClose" aria-label="Close dialog">
          <Icon name="interface_close" :size="24" />
        </button>
      </div>

      <div class="text-content">
        <h2 class="dialog-title-modal">Give Badge for Responsibility</h2>
        <p v-if="description" class="dialog-description">{{ description }}</p>
      </div>

      <!-- Content -->
      <div class="dialog-content">
        <BadgeLevelPicker v-model="selectedLevel" @change="handleLevelChange" />

        <div class="notify-section">
          <div v-if="notifyEmployee && canNotify" class="notify-checkbox-container">
            <input type="checkbox" id="notify-employee" v-model="notifyEmployee" class="notify-checkbox" />
            <label for="notify-employee" class="notify-label">
              Notify Employee
            </label>
          </div>
          <p v-if="notifyEmployee && canNotify" class="notify-description">
            The employee will be notified via email
          </p>
          <div class="give-badge-notification-warning" v-if="!canNotify">
            <img class="give-badge-warning-icon" :src="require('@components/assets/notify-outer.svg')" alt="warning icon"
              width="50" height="50" object-fit="contain" />
            <span class="give-badge-warning-text">Employee cannot be notified <br> because it does not have
              access</span>
          </div>
        </div>
      </div>

      <!-- Modal Actions -->
      <div class="modal-actions">
        <div class="actions-content">
          <div class="actions-right">
            <button class="give-badge-button" :disabled="!isValid" @click="handleGiveBadge">
              Give Badge
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch } from "vue";
import Icon from "@components/icons/Icon.vue";
import BadgeLevelPicker from "@components/BadgeLevelPicker/BadgeLevelPicker.vue";

const props = defineProps({
  value: {
    type: Boolean,
    default: false,
  },
  description: {
    type: String,
    default: "",
  },
  employeeName: {
    type: String,
    default: "",
  },
  responsibilityName: {
    type: String,
    default: "",
  },
  canNotify: {
    type: Boolean,
    default: true,
  },
  defaultLevel: {
    type: String,
    default: "apprentice",
    validator: (value) => ["apprentice", "professional", "coach", "master"].includes(value),
  },
});

const emit = defineEmits(["input", "give-badge", "close"]);

const selectedLevel = ref(props.defaultLevel);
const notifyEmployee = ref(true);

const isValid = computed(() => {
  return selectedLevel.value !== null;
});

watch(
  () => props.value,
  (newVal) => {
    if (newVal) {
      // Reset to defaults when dialog opens
      selectedLevel.value = props.defaultLevel;
      notifyEmployee.value = props.canNotify;
    }
  }
);

watch(
  () => props.defaultLevel,
  (newVal) => {
    selectedLevel.value = newVal;
  }
);

const handleLevelChange = (level) => {
  selectedLevel.value = level;
};

const handleOverlayClick = () => {
  handleClose();
};

const handleClose = () => {
  emit("input", false);
  emit("close");
};

const handleGiveBadge = () => {
  if (isValid.value) {
    emit("give-badge", {
      level: selectedLevel.value,
      notifyEmployee: notifyEmployee.value && props.canNotify,
    });
  }
};
</script>

<style scoped>
.give-badge-dialog-overlay {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.6);
  backdrop-filter: blur(4px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
}

.give-badge-notification-warning {
  display: flex !important;
  align-items: center !important;
  gap: 8px !important;
  flex-wrap: nowrap !important;
}

.give-badge-warning-icon {
  flex-shrink: 0 !important;
  width: 50px !important;
  height: 50px !important;
  object-fit: contain !important;
}

.give-badge-warning-text {
  font-family: Inter, sans-serif !important;
  font-weight: 400 !important;
  font-size: 14px !important;
  line-height: 20px !important;
  letter-spacing: 0 !important;
  color: #344054 !important;
  margin: 0 !important;
  white-space: nowrap !important;
  text-align: left !important;
}

.give-badge-dialog {
  background: white;
  background-image: url("@components/assets/Background_pattern_decorative_new.svg");
  background-repeat: no-repeat;
  background-position: top left;
  border-radius: 12px;
  width: 100%;
  max-width: 500px;
  max-height: 90vh;
  overflow-y: auto;
  box-shadow: 0px 8px 8px -4px rgba(16, 24, 40, 0.08),
    0px 20px 24px -4px rgba(16, 24, 40, 0.14);
  position: relative;
}

.modal-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  position: relative;
  padding: 24px 24px 0 24px;
  width: 100%;
  z-index: 1;
}

.header-content {
  display: flex;
  align-items: center;
  gap: 12px;
  flex: 1;
}

.featured-icon {
  background: white;
  border: 1px solid #eaecf0;
  border-radius: 10px;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.text-content {
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 0 24px;
  margin-top: 16px;
  margin-bottom: 24px;
}

.dialog-title-modal {
  font-family: "Inter", sans-serif;
  font-weight: 600;
  font-size: 18px;
  line-height: 28px;
  color: #101828;
  text-align: center;
  margin: 0;
}

.dialog-description {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #475467;
  text-align: center;
  margin: 0;
}

.close-button {
  position: absolute;
  top: 16px;
  right: 16px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px;
  border-radius: 8px;
  border: none;
  background: transparent;
  cursor: pointer;
  color: #98a2b3;
  transition: background-color 0.2s;
}

.close-button:hover {
  background-color: rgba(152, 162, 179, 0.1);
}

.dialog-content {
  padding: 0 24px;
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 24px;
}

.notify-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.notify-checkbox-container {
  display: flex;
  align-items: center;
  gap: 8px;
}

.notify-checkbox {
  width: 20px;
  height: 20px;
  border-radius: 6px;
  border: 1.5px solid #7a5af8;
  background-color: #ffffff;
  cursor: pointer;
  appearance: none;
  -webkit-appearance: none;
  -moz-appearance: none;
  position: relative;
  flex-shrink: 0;
}

.notify-checkbox:checked {
  background-color: #7a5af8;
  border-color: #7a5af8;
}

.notify-checkbox:checked::after {
  content: "";
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  width: 12px;
  height: 12px;
  background-image: url("data:image/svg+xml,%3Csvg width='12' height='12' viewBox='0 0 12 12' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M10 3L4.5 8.5L2 6' stroke='white' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'/%3E%3C/svg%3E");
  background-repeat: no-repeat;
  background-position: center;
}

.notify-checkbox:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.notify-label {
  font-family: "Inter", sans-serif;
  font-weight: 500;
  font-size: 14px;
  line-height: 20px;
  color: #344054;
  cursor: pointer;
  user-select: none;
}

.notify-description {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #98a2b3;
  margin: 0;
  margin-left: 28px;
}

.notify-warning {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: 28px;
  padding: 8px 12px;
  background-color: #fff4ed;
  border-radius: 8px;
}

.warning-icon {
  color: #f79009;
  flex-shrink: 0;
}

.warning-text {
  font-family: "Inter", sans-serif;
  font-weight: 400;
  font-size: 14px;
  line-height: 20px;
  color: #b93815;
}

.modal-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  padding-top: 0;
  width: 100%;
  margin-top: 32px;
}

.actions-content {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 0 24px 24px 24px;
  width: 100%;
}

.actions-right {
  display: flex;
  flex: 1 0 0;
  gap: 12px;
  align-items: center;
  justify-content: stretch;
  min-width: 0;
  width: 100%;
}

.give-badge-button {
  display: flex;
  padding: 10px 16px;
  justify-content: center;
  align-items: center;
  gap: 10px;
  flex: 1 0 0;
  border-radius: var(--radius-md, 8px);
  border: 1px solid var(--Black, #000);
  background: #000;
  color: #fff;
  /* Shadows/shadow-xs */
  box-shadow: 0 1px 2px 0 rgba(16, 24, 40, 0.05);
}

.give-badge-button:hover:not(:disabled) {
  background-color: #1a1a1a;
}

.give-badge-button:disabled {
  background-color: #d0d5dd;
  color: #98a2b3;
  cursor: not-allowed;
  opacity: 0.6;
}
</style>
